import { Button, FormCard } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { eolSectionConfig } from './eolSectionConfig'
import type { EolStructuredSection } from './eolTypes'
import {
  type PlaceOfCareSnomedCode,
  PlaceOfCareSnomedDescriptionEnum
} from './eolTypes'

interface EolSectionCardProps {
  section: EolStructuredSection
  isViewMode?: boolean
  index?: number
  onRemove?: () => void
  onEdit?: () => void
}

const formatDateValue = (value: string | undefined): string => {
  if (!value) return ''
  const parsed = dayjs(value)
  if (parsed.isValid()) {
    return parsed.hour() || parsed.minute()
      ? parsed.format('DD-MM-YYYY HH:mm')
      : parsed.format('DD-MM-YYYY')
  }
  return value
}

const EolSectionCard: React.FC<EolSectionCardProps> = ({
  section,
  isViewMode = false,
  index = 0,
  onRemove,
  onEdit
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const config = eolSectionConfig.find(
    (c) => c.sectionType === section.sectionType
  )

  if (!config) return null

  return (
    <StyledFormCard
      title={section.label || config.label}
      testId={`eol-section-card-${index}`}
      rightChildren={
        !isViewMode && (
          <ActionRow>
            {onEdit && (
              <Button
                type="button"
                variant="text"
                onClick={onEdit}
                data-testid={`eol-section-edit-${index}`}
              >
                {t(carePlansConstants.EOL_EDIT)}
              </Button>
            )}
            {onRemove && (
              <Button
                type="button"
                variant="textDanger"
                onClick={onRemove}
                data-testid={`eol-section-remove-${index}`}
              >
                {t(carePlansConstants.EOL_REMOVE)}
              </Button>
            )}
          </ActionRow>
        )
      }
    >
      {section.entries.map((entry, entryIndex) => (
        <EntryBlock key={entryIndex}>
          {section.entries.length > 1 && (
            <EntryNumber weight="medium">
              {t(carePlansConstants.EOL_ACTION_LABEL, {
                number: entryIndex + 1
              })}
            </EntryNumber>
          )}
          {config.fields.map((field) => {
            const rawValue = entry[field.key]
            if (!rawValue && isViewMode) return null

            let displayValue = rawValue || ''
            if (field.type === 'select' && rawValue) {
              displayValue =
                PlaceOfCareSnomedDescriptionEnum[
                  rawValue as PlaceOfCareSnomedCode
                ] || rawValue
            }
            if (field.type === 'date' && rawValue) {
              displayValue = formatDateValue(rawValue)
            }

            const hideLabel =
              config.fields.length === 1 && field.type === 'textarea'

            return (
              <FieldRow key={field.key}>
                {!hideLabel && (
                  <FieldLabel weight="medium">{field.label}</FieldLabel>
                )}
                <FieldValue>{displayValue}</FieldValue>
              </FieldRow>
            )
          })}
          {entryIndex < section.entries.length - 1 && <EntryDivider />}
        </EntryBlock>
      ))}
    </StyledFormCard>
  )
}

const StyledFormCard = styled(FormCard)`
  margin-bottom: 0;
`

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EntryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EntryNumber = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
  text-transform: uppercase;
  margin-bottom: 4px;
`

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const FieldLabel = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
  margin-bottom: 0;
`

const FieldValue = styled(Text)`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color2};
  white-space: pre-wrap;
  margin-bottom: 0;
`

const EntryDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.neutral.color8};
  margin: 8px 0;
`

export default EolSectionCard
